import React, { useCallback, useContext } from 'react';

import { Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { MapDataContext } from '../context/map-data';
import { DataSourceModel, ModelTypes } from '../core/global';
import { ModelShape, ModelShapeItem } from '../data/layers';
import { trackEvent } from '../helpers/tracking';
import useIsSuperAdmin from '../hooks/useIsSuperAdmin';
import styles from "../styles/components/models-switch.module.scss";

const ENABLE_DYNAMIC_SCALE = false 

interface ModelSelectProps {
  onChange: (newModel: ModelTypes) => void
  value: string
  id: string
  label?: string
  dataSources: ModelShape | null
}

export const ModelSelect = ({
  onChange,
  value,
  id,
  label = '',
  dataSources,
}: ModelSelectProps) => {


  const SelectElement = useCallback( () => {
    if (!dataSources) {
      return null
    }

    return (
      <Select
        style={{width: '100%'}}
        labelId={id}
        id={id}
        value={value}
        label={label}
        onChange={(event: SelectChangeEvent) => {
            const newModel = event.target.value as string
            onChange(newModel)
          }
        }
      >
      {
        Object.keys(dataSources).map((shapeKey: string) => {
          const item: ModelShapeItem = dataSources[shapeKey]
          const {name} = item
          return (
            <MenuItem
              key={`${shapeKey}`} 
              value={shapeKey}
            >
            {name}
          </MenuItem>
          )
        })
      }
      </Select>
    )
}, [JSON.stringify(dataSources), onChange, value, id, label])

  if (label) {
    return (
      <FormControl
        className={styles.formWrapper}  
      >
          <InputLabel id={id}>{label}</InputLabel>
          <SelectElement />
      </FormControl>
    )
  }
  return (
    <SelectElement />
  )
}

export const ModelSwitch = () => {

  const {
    getFeatureLocation,
    setDataSourceModel,
    dataSourceModel,
    updateMeshData,
    setShowValidations,
    showDynamicScaling,
    setShowDynamicScaling,
    showValidations,
    showModelGrid,
    hasModelGrid,
    // hasAverages,
    setShowModelGrid,
    dataSources,
    setScaleValues,
    // setShowAverages,
    // showAverages,
    showValidationsSuperAdmin,
    setShowValidationsSuperAdmin,
    setCurrentSelectionParams,
  } = useContext(MapDataContext);

  const isSuperAdmin = useIsSuperAdmin();
  
  const runUpdateOnMeshData = (newModelKey: string, lat?: number | null, lon?: number | null, useShowAverages?: boolean | null | undefined) => {
    const [provider, model, geography, version = null] = newModelKey.split('-')
    const newModel = {
      key: newModelKey,
      provider,
      model,
      geography,
      version,
    } as DataSourceModel
    setDataSourceModel(newModel);
    setCurrentSelectionParams(null);
    setScaleValues(null)
    const useLat = lat
    const useLon = lon

    if (useLat && useLon) {
      const coords = `${useLat},${useLon}`
      updateMeshData({coords, useModel: newModel, showAverages: useShowAverages})
      trackEvent("model-change", {lat: useLat, lon: useLon, provider, version, model, geography})
    }
  }

  if (dataSourceModel === null || dataSources === null) {
    return null
  }

  return (
    <div className={styles.container}>
      <ModelSelect
        dataSources={dataSources}
        id="model-select"
        value={`${dataSourceModel.key}`}
        onChange={(newModelKey: string) => {
          const {
            lat,
            lon,
          } =  getFeatureLocation()
          runUpdateOnMeshData(newModelKey, lat, lon)
        }}
      />
      {ENABLE_DYNAMIC_SCALE && <FormControlLabel
        sx={{mt: '16px', mb: '-8px !important'}}
        disabled={!!(dataSources && dataSources[dataSourceModel.key].vector_tiles === null)}
        control={
          <Checkbox
            value="dynamic-scaling" 
            checked={!(dataSources && dataSources[dataSourceModel.key].vector_tiles === null) && showDynamicScaling}
            onChange={(e: React.SyntheticEvent) => {
              setShowDynamicScaling( i => !i)
            }}
            color="primary" 
          />}
        label="Show dynamic scale (lower zoom levels)"
      />}
      <FormControlLabel
        sx={{mt: '16px', mb: '-8px !important'}}
        control={
          <Checkbox
            value="validations" 
            checked={showValidations}
            onChange={(e: React.SyntheticEvent) => {
              setShowValidations( i => !i)
            }}
            color="primary" 
          />}
        label="Show validations"
      />
      {<FormControlLabel
        sx={{mt: '16px', mb: 0}}
        control={
          <Checkbox
            disabled={!hasModelGrid}
            value="model-grid" 
            checked={showModelGrid && hasModelGrid}
            onChange={(e: React.SyntheticEvent) => {
              setShowModelGrid( i => !i)
            }}
            color="primary" 
          />}
        label={`Show model grid ${hasModelGrid ? '' : '(not available)'}`}
      />}
      {isSuperAdmin && <FormControlLabel
        sx={{mt: '16px', mb: 0}}
        control={
          <Checkbox
            value="model-grid" 
            checked={showValidationsSuperAdmin}
            onChange={(e: React.SyntheticEvent) => {
              setShowValidationsSuperAdmin( i => !i)
            }}
            color="primary" 
          />}
        label={`Show all private validations (super admin)`}
      />}
      {/* {hasAverages === true && <FormControlLabel
        sx={{mt: '16px', mb: 0}}
        control={
          <Checkbox
            // disabled={!hasModelGrid}
            value="model-grid" 
            checked={showAverages}
            onChange={(e: React.SyntheticEvent) => {
              const show = !showAverages
              const {
                lat,
                lon,
              } =  getFeatureLocation()
              runUpdateOnMeshData(dataSourceModel.key, lat, lon, show)
              setShowAverages(show)
            }}
            color="primary" 
          />}
        label={`Interpolate`}
      />} */}
    </div>
  )
}
